import React from 'react';
import { motion } from 'framer-motion';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useScrollToSection } from '../../hooks/useScrollToSection';
import { staggerContainer, staggerItem, fadeInLeft, fadeInRight } from '../../utils/animation';
import { IconTrendingUp, IconDatabase, IconCloud, IconUsers } from '../ui/Icons';
import './About.css';

const STATS = [
  { value: '7+',    label: 'Years Experience' },
  { value: '1M+',   label: 'Users Served' },
  { value: '9+',    label: 'Asian Markets' },
  { value: '98%',   label: 'AI Accuracy' },
];

const PILLARS = [
  {
    icon: <IconDatabase size={20} />,
    title: 'Lakehouse & Data Platforms',
    text: 'Petabyte-scale architectures on BigQuery and Snowflake with Dataform, dbt, Airflow, and Spark - 40% latency reduction on production systems.',
  },
  {
    icon: <IconTrendingUp size={20} />,
    title: 'GenAI & RAG Pipelines',
    text: 'Production RAG pipelines with LangChain and vector databases, domain-specific LLM fine-tuning, and serverless AI inference at 98% extraction accuracy.',
  },
  {
    icon: <IconCloud size={20} />,
    title: 'Cloud Architecture & FinOps',
    text: '25–30% monthly cost reductions through FinOps audits and IaC (Terraform / CloudFormation) across AWS, GCP, and Azure with 99.99% uptime.',
  },
  {
    icon: <IconUsers size={20} />,
    title: 'Engineering Leadership',
    text: 'Led cross-functional teams of 10+ engineers with Agile delivery, CI/CD pipelines, and compliance (GDPR · HIPAA · HL7) across pharma and fintech.',
  },
];

export const About: React.FC = () => {
  const { ref, inView } = useIntersectionObserver({ threshold: 0.08, triggerOnce: true });
  const scrollToSection = useScrollToSection();

  return (
    <section id="about" className="about section section--dark" ref={ref}>
      <div className="container">
        <div className="about__layout">

          {/* Left */}
          <motion.div
            className="about__left"
            variants={fadeInLeft}
            initial="initial"
            animate={inView ? 'animate' : 'initial'}
          >
            <span className="section__subtitle">ABOUT</span>
            <h2 className="about__heading">
              Building systems<br />
              <span className="about__heading-accent">that matter</span>
            </h2>
            <p className="about__bio">
              Over 7 years I've evolved from backend engineering to leading end-to-end AI
              and data platform architecture for enterprise clients across Asia. My edge is
              owning <strong>production GenAI, modern data platforms, and cloud infrastructure</strong> hands-on -
              from RAG pipelines and LLM fine-tuning to petabyte Lakehouses and multi-cloud FinOps.
            </p>
            <div className="about__focus-pill">
              <span className="about__focus-dot" />
              Currently: Solutions Architect at NextZen Minds - AI &amp; Data for Healthcare &amp; Fintech
            </div>

            <div className="about__stats-grid">
              {STATS.map(s => (
                <motion.div
                  key={s.label}
                  className="about__stat"
                  initial={{ opacity: 0, scale: 0.88 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.5, ease: [0.22,1,0.36,1] }}
                >
                  <span className="about__stat-value">{s.value}</span>
                  <span className="about__stat-label">{s.label}</span>
                </motion.div>
              ))}
            </div>

            <motion.button
              className="btn btn--primary"
              onClick={() => scrollToSection('contact')}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 400, damping: 18 }}
              style={{ alignSelf: 'flex-start', marginTop: 'var(--spacing-2)' }}
            >
              Let's Collaborate
            </motion.button>
          </motion.div>

          {/* Right */}
          <motion.div
            className="about__right"
            variants={fadeInRight}
            initial="initial"
            animate={inView ? 'animate' : 'initial'}
          >
            <motion.div
              className="about__pillars"
              variants={staggerContainer}
              initial="initial"
              animate={inView ? 'animate' : 'initial'}
            >
              {PILLARS.map(p => (
                <motion.div key={p.title} className="about__pillar" variants={staggerItem}>
                  <div className="about__pillar-icon">{p.icon}</div>
                  <div>
                    <h4 className="about__pillar-title">{p.title}</h4>
                    <p className="about__pillar-text">{p.text}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};
